import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Shield, ArrowLeft } from "lucide-react";

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    try {
      const trainerData = await login(email, password);
      
      // Verify admin role
      if (!trainerData.is_admin) {
        toast.error("This login is for administrators only");
        return;
      }
      
      toast.success("Welcome back, Admin!");
      navigate("/admin");
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Trainer Login</span>
        </Link>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md glass-card border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 border border-primary/20">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="font-heading text-2xl tracking-tight uppercase">
              Admin Login
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Access the admin dashboard
            </p>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                  Admin Email
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@fittrackcoach.in"
                  className="h-12 bg-secondary/50 border-transparent"
                  data-testid="admin-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                  Password
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 bg-secondary/50 border-transparent"
                  data-testid="admin-password-input"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl mt-6"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? <div className="spinner" /> : "Access Admin Panel"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="p-4 text-center">
        <p className="text-xs text-muted-foreground">
          Tracked with <span className="text-primary font-medium">FitTrack Coach</span>
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          <a href="https://app.fittrackcoach.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">app.fittrackcoach.in</a>
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">by InfiNest Tech Pvt. Ltd.</p>
      </footer>
    </div>
  );
}
